import {
  queryOptions,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import {
  assignTeamTags,
  createTeamTag,
  deleteTeamTag,
  getTeamTags,
  listTeamTagOptions,
  listTeamTags,
  updateTeamTag,
} from '@/modules/team-tag/team-tag.service'
import { TEAM_TAG_QUERY_KEYS } from '@/modules/team-tag/team-tag.constants'
import type {
  TCreateTeamTagRequest,
  TTeamTag,
  TUpdateTeamTagRequest,
} from '@/modules/team-tag/team-tag.types'
import type { TAdminListParams } from '@/core/lib/table-query'

type TMutationError = AxiosError<{ code?: string; message?: string }>

export const teamTagListQueryOptions = (params: TAdminListParams) =>
  queryOptions({
    queryKey: TEAM_TAG_QUERY_KEYS.list(params),
    queryFn: () => listTeamTags(params),
    staleTime: 1000 * 30,
  })

export const teamTagOptionsQueryOptions = () =>
  queryOptions({
    queryKey: TEAM_TAG_QUERY_KEYS.options,
    queryFn: listTeamTagOptions,
    staleTime: 1000 * 60 * 5,
  })

export const teamTagsForTeamQueryOptions = (teamId: string) =>
  queryOptions({
    queryKey: TEAM_TAG_QUERY_KEYS.teamTags(teamId),
    queryFn: () => getTeamTags(teamId),
    staleTime: 1000 * 30,
  })

export const useCreateTeamTag = (
  options?: UseMutationOptions<TTeamTag, TMutationError, TCreateTeamTagRequest>
) => useMutation({ mutationFn: createTeamTag, ...(options ?? {}) })

export const useUpdateTeamTag = (
  options?: UseMutationOptions<
    TTeamTag,
    TMutationError,
    { id: string; payload: TUpdateTeamTagRequest }
  >
) =>
  useMutation({
    mutationFn: ({ id, payload }) => updateTeamTag(id, payload),
    ...(options ?? {}),
  })

export const useDeleteTeamTag = (
  options?: UseMutationOptions<void, TMutationError, string>
) => useMutation({ mutationFn: deleteTeamTag, ...(options ?? {}) })

export const useAssignTeamTags = (
  options?: UseMutationOptions<
    TTeamTag[],
    TMutationError,
    { teamId: string; tagIds: string[] }
  >
) =>
  useMutation({
    mutationFn: ({ teamId, tagIds }) => assignTeamTags(teamId, tagIds),
    ...(options ?? {}),
  })
